import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { PALETTES } from "@/lib/eventOptions";
import { themeMotifConfig, ThemeOrnament } from "@/lib/themeMotif";
import { publicImageUrl } from "@/components/ImageUpload";

/**
 * An A5-ratio (148 × 210mm, ~1:√2) print-ready invitation card.
 * Renders off-screen; html2canvas snapshots it; jsPDF wraps the image in an A5 PDF.
 *
 * Everything online-only (countdown, RSVP form, map, guestbook link, footer, nav)
 * is intentionally omitted. A QR code + short URL is included so printed guests
 * can still tap their way to the live invite to RSVP.
 */
const PrintableInviteCard = React.forwardRef(({ event, publicUrl }, ref) => {
  if (!event) return null;
  const palette = PALETTES.find((p) => p.name === event.palette) || PALETTES[0];
  const motif = themeMotifConfig(event.theme, event.custom_theme);
  const bannerUrl = event.banner_path ? publicImageUrl(event.banner_path) : null;

  const L = (v, d) => (v && String(v).trim()) ? v : d;
  const introText = L(event.intro_label, `You're invited to a ${(event.event_type || "").replace("_", " ")}`);
  const hostedByText = L(event.hosted_by_label, "Hosted by");
  const whenText = L(event.when_label, "When");
  const whereText = L(event.where_label, "Where");
  const attireText = L(event.attire_label, "Attire");
  const aboutText = L(event.about_label, "About");
  const hostMsgText = L(event.host_message_label, "A word from your host");

  let dateStr = ""; let timeStr = "";
  try { const d = new Date(event.event_datetime); dateStr = format(d, "EEEE, MMMM d, yyyy"); timeStr = format(d, "p"); } catch {}

  // A5 at 150dpi → 874 × 1240 px. Render a bit larger; html2canvas will scale up.
  const W = 980;
  const H = Math.round(W * Math.SQRT2); // A5 ratio

  return (
    <div
      ref={ref}
      data-testid="printable-invite"
      style={{
        width: W,
        minHeight: H,
        background: palette.bg,
        color: palette.primary,
        fontFamily: "'Manrope', sans-serif",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top illustrated band (if busy) */}
      {event.motif_density === "busy" && event.theme_art_top_url ? (
        <img
          src={event.theme_art_top_url}
          alt=""
          crossOrigin="anonymous"
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: "3 / 1",
            objectFit: "cover",
            objectPosition: "center top",
            mixBlendMode: "multiply",
            display: "block",
          }}
        />
      ) : null}

      {/* Optional host photo banner */}
      {bannerUrl ? (
        <img
          src={bannerUrl}
          alt=""
          crossOrigin="anonymous"
          style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }}
        />
      ) : null}

      {/* Card body */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 64px 24px" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", opacity: 0.7 }}>{introText}</div>

        {event.show_theme_motif !== false && (
          <div style={{ marginTop: 20 }}>
            <ThemeOrnament config={motif} color={palette.primary} />
          </div>
        )}

        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: 72,
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            marginTop: 18,
            marginBottom: 0,
            textAlign: "center",
            maxWidth: "100%",
            wordBreak: "break-word",
          }}
        >
          {event.title}
        </h1>

        {event.show_host_name !== false && event.host_name ? (
          <div style={{ marginTop: 44, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.8 }}>
            {hostedByText} {event.host_name}
          </div>
        ) : null}

        {/* rule */}
        <div style={{ width: 80, height: 1, opacity: 0.35, background: palette.primary, marginTop: 32 }} />

        {/* Date & time */}
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7 }}>{whenText}</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 34, marginTop: 8 }}>{dateStr}</div>
          {timeStr ? <div style={{ fontSize: 16, opacity: 0.8, marginTop: 4 }}>{timeStr}</div> : null}
        </div>

        {/* Location */}
        <div style={{ marginTop: 28, textAlign: "center" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7 }}>{whereText}</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, marginTop: 8 }}>{event.location_name}</div>
          {event.location_address ? (
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4, maxWidth: 480 }}>{event.location_address}</div>
          ) : null}
        </div>

        {/* Attire & description, if shown */}
        {event.show_dress_code !== false && event.dress_code ? (
          <div style={{ marginTop: 22, textAlign: "center" }}>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7 }}>{attireText}</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, marginTop: 4 }}>{event.dress_code}</div>
          </div>
        ) : null}

        {event.show_description !== false && event.description ? (
          <div style={{ marginTop: 22, textAlign: "center", maxWidth: 600 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7 }}>{aboutText}</div>
            <p style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6, opacity: 0.9, whiteSpace: "pre-wrap" }}>{event.description}</p>
          </div>
        ) : null}

        {event.show_host_message !== false && event.host_message ? (
          <div style={{ marginTop: 22, textAlign: "center", maxWidth: 600 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7 }}>{hostMsgText}</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 20, marginTop: 6, opacity: 0.95, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
              &ldquo;{event.host_message}&rdquo;
            </p>
          </div>
        ) : null}

        {/* QR + RSVP prompt — sticks to the bottom */}
        <div style={{ flex: 1 }} />
        {publicUrl ? (
          <div style={{ marginTop: 28, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ background: "white", padding: 10, border: `1px solid ${palette.primary}22` }}>
              <QRCodeSVG value={publicUrl} size={120} bgColor="#ffffff" fgColor={palette.primary} />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.7, marginTop: 10 }}>Scan to RSVP</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{publicUrl.replace(/^https?:\/\//, "")}</div>
          </div>
        ) : null}
      </div>

      {/* Bottom illustrated band */}
      {event.motif_density === "busy" && event.theme_art_bottom_url ? (
        <img
          src={event.theme_art_bottom_url}
          alt=""
          crossOrigin="anonymous"
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: "3 / 1",
            objectFit: "cover",
            objectPosition: "center bottom",
            mixBlendMode: "multiply",
            display: "block",
          }}
        />
      ) : null}
    </div>
  );
});
PrintableInviteCard.displayName = "PrintableInviteCard";
export default PrintableInviteCard;
